import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { FaceHighlight } from './FaceHighlight'
import { boxPercent } from './highlight'

/** What the analyst has decided about one face — the box's tone follows it. */
export type DocumentFaceBoxState = 'confirmed' | 'suggested' | 'unassigned'

/** Each state's box, as token classes only (slice 06). */
const STATE_TONE: Record<DocumentFaceBoxState, string> = {
  confirmed: 'border-success ring-success/30',
  suggested: 'border-warning ring-warning/30',
  unassigned: 'border-muted-foreground/70 ring-background/50',
}

export interface DocumentFaceLayerBox {
  faceId: string
  /** `[x1, y1, x2, y2]` in 0–1 of this image. */
  box: readonly number[]
  /** Already translated label of this box, for assistive tech. */
  label: string
  state: DocumentFaceBoxState
}

export interface DocumentFaceLayerProps {
  /** The rendered image: a blob URL the parent resolved (auth-gated bytes). */
  imageUrl: string | null
  /** Alt text, already translated. */
  alt: string
  /** Every face of this image, whatever its state. */
  boxes: readonly DocumentFaceLayerBox[]
  /** The face to emphasise; null draws every box the same. */
  focusedFaceId: string | null
  /** Picking a box selects its row in the rail; omit for a read-only layer. */
  onSelect?: (faceId: string) => void
  className?: string
}

/**
 * One document image with every face boxed, styled by state, and the focused one
 * emphasised (PRD-0031 slices 03 and 06).
 *
 * The image and the focused box are `FaceHighlight` from PRD-0030 — same
 * component, same coordinate basis — and the remaining faces ride in its box
 * layer, so a group photo reads as "this face, among these" and every box sits
 * on the painted image whatever shape the slot has. Pure: the boxes, their
 * labels and the selection all arrive as props.
 */
export function DocumentFaceLayer({
  imageUrl,
  alt,
  boxes,
  focusedFaceId,
  onSelect,
  className,
}: DocumentFaceLayerProps) {
  const { t } = useTranslation()
  const focused = focusedFaceId ? boxes.find(entry => entry.faceId === focusedFaceId) : undefined
  const others = boxes.filter(entry => entry.faceId !== focused?.faceId)
  const focusedRect = focused ? boxPercent(focused.box) : null
  return (
    <div className={cn('relative', className)} data-testid="document-face-layer">
      <FaceHighlight
        imageUrl={imageUrl}
        alt={alt}
        box={focused?.box ?? null}
        boxLabel={focused?.label ?? alt}
        className="absolute inset-0"
      >
        {others.map(entry => {
          const rect = boxPercent(entry.box)
          return rect ? (
            <FaceBox
              key={entry.faceId}
              entry={entry}
              rect={rect}
              onSelect={onSelect}
              selectLabel={t('documents.detail.faces.selectBox')}
            />
          ) : null
        })}
        {/* The focused box is drawn by FaceHighlight; this invisible twin only
            makes it clickable, so picking the emphasised face selects its row too. */}
        {focused && focusedRect && onSelect ? (
          <button
            type="button"
            aria-label={t('documents.detail.faces.selectBox')}
            data-face-box={focused.faceId}
            data-face-state={focused.state}
            data-focused="true"
            onClick={() => onSelect(focused.faceId)}
            className="absolute cursor-pointer bg-transparent"
            style={{ ...focusedRect }}
          />
        ) : null}
      </FaceHighlight>
    </div>
  )
}

function FaceBox({
  entry,
  rect,
  onSelect,
  selectLabel,
}: {
  entry: DocumentFaceLayerBox
  rect: NonNullable<ReturnType<typeof boxPercent>>
  onSelect?: (faceId: string) => void
  selectLabel: string
}) {
  const shared = cn('absolute rounded-sm border ring-1', STATE_TONE[entry.state])
  if (!onSelect) {
    return (
      <div
        role="img"
        aria-label={entry.label}
        data-testid="document-face-box"
        data-face-box={entry.faceId}
        data-face-state={entry.state}
        className={shared}
        style={{ ...rect }}
      />
    )
  }
  return (
    <button
      type="button"
      title={entry.label}
      aria-label={`${entry.label} — ${selectLabel}`}
      data-testid="document-face-box"
      data-face-box={entry.faceId}
      data-face-state={entry.state}
      onClick={() => onSelect(entry.faceId)}
      className={cn(shared, 'cursor-pointer hover:border-primary')}
      style={{ ...rect }}
    />
  )
}
